import { convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'
import {
  InvalidateQueryFilters,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  ForecastsAndInventoriesWithPagesProps,
  SupplierForecastListWithPagesProps,
  SupplierForecastProps,
  SupplierProductListWithPagesProps,
  SupplierProductMetricsProps,
  SupplierProductProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  BFF_FORECAST_URL,
  BFF_FORECASTS_AND_INVENTORIES_URL,
  BFF_FORECASTS_URL,
  BFF_SUPPLIER_PRODUCT_URL,
  BFF_SUPPLIER_PRODUCTS_METRICS_URL,
  BFF_SUPPLIER_PRODUCTS_URL,
  MULTIPLES_SUPPLIER_PRODUCTS_URL,
} from './api.urls'

const supplierProductPermittedParams = [
  'page',
  'limit',
  'search',
  'supplierUuid',
  'supplier_Uuid',
  'offset',
  'ordering',
  'createdAtBefore',
  'createdAtAfter',
  'agreementReference',
  'agreementType',
  'batchCode',
  'catalogProduct_Uuid',
  'costExpirationDateBefore',
  'costExpirationDateAfter',
  'entryDateBefore',
  'entryDateAfter',
  'internalCode',
  'package',
  'productTitle',
  'reference',
  'supplierEmail',
  'supplierName',
  'supplierWarehouse_Uuid',
]

const forecastPermittedParams = [
  'limit',
  'offset',
  'ordering',
  'search',
  'agreementReference',
  'agreementType',
  'reference',
  'supplierProduct_Reference',
  'supplierProduct_Supplier_Email',
  'supplierProduct_Supplier_Name',
  'supplierProduct_Supplier_Uuid',
  'supplierProduct_CatalogProduct_Uuid',
  'supplierProduct_CatalogProduct_Attributes_Title',
  'expiresAtBefore',
  'expiresAtAfter',
  'costMin',
  'costMax',
  'quantityMin',
  'quantityMax',
]

export const useSupplierProductsList = ({
  params,
  isEnable = true,
}: {
  params?: Record<string, string | number>
  isEnable?: boolean
}) => {
  const getSupplierProductsList = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierProductListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && supplierProductPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }
        return acc
      }, {})

      const { data } = await api.get(BFF_SUPPLIER_PRODUCTS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Suppliers Products fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as SupplierProductListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get suppliers products!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module, params],
    enabled: isEnable,
    queryFn: () => getSupplierProductsList(),
  })
}

export const useSupplierProductDetail = (uuid?: string) => {
  const getSupplierDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierProductProps
  }> => {
    try {
      const { data } = await api.get(BFF_SUPPLIER_PRODUCT_URL(uuid || ''))

      return {
        success: true,
        message: 'Supplier products fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supplier products!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module, uuid],
    queryFn: () => getSupplierDetail(),
    enabled: !!uuid,
  })
}

export const useCreateSupplierProduct = () => {
  const createSupplier = async (
    payload: Partial<SupplierProductProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierProductProps
  }> => {
    try {
      const { data } = await api.post(BFF_SUPPLIER_PRODUCTS_URL, payload)

      return {
        success: true,
        message: 'Supplier products created successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create supplier products!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module],
    mutationFn: (payload: Partial<SupplierProductProps>) =>
      createSupplier(payload),
  })
}

export const useUpdateSupplierProduct = () => {
  const updateSupplier = async (
    payload: Partial<SupplierProductProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierProductProps
  }> => {
    try {
      const { data } = await api.patch(
        `${BFF_SUPPLIER_PRODUCTS_URL}${payload.uuid}/`,
        { ...payload, uuid: undefined },
      )

      return {
        success: true,
        message: 'Supplier products updated successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update supplier products!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module],
    mutationFn: (payload: Partial<SupplierProductProps>) =>
      updateSupplier(payload),
  })
}

export const useDeleteSupplierProduct = () => {
  const queryClient = useQueryClient()

  const deleteSupplierProduct = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierProductProps
  }> => {
    try {
      const { data } = await api.delete(`${BFF_SUPPLIER_PRODUCTS_URL}${uuid}/`)

      return {
        success: true,
        message: 'Supplier product deleted successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to deleted supplier product!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module],
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS.module,
      ] as InvalidateQueryFilters)
    },
    mutationFn: (uuid: string) => deleteSupplierProduct(uuid),
  })
}

export const useCreateMultiplesSupplierProducts = () => {
  const queryClient = useQueryClient()

  const createSupplier = async (
    payload: Partial<SupplierProductProps>[],
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierProductProps
  }> => {
    try {
      const { data } = await api.post(
        MULTIPLES_SUPPLIER_PRODUCTS_URL,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Supplier products created successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create supplier products!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.SUPPLIER_PRODUCTS.module],
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERIES_ADMIN.SUPPLIER_PRODUCTS.module,
      ] as InvalidateQueryFilters)
    },
    mutationFn: (payload: Partial<SupplierProductProps>[]) =>
      createSupplier(payload),
  })
}

export const useSupplierProductMetrics = () => {
  const getSupplierMetrics = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierProductMetricsProps
  }> => {
    try {
      const { data } = await api.get(BFF_SUPPLIER_PRODUCTS_METRICS_URL)

      return {
        success: true,
        message: 'Supplier products metrics fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplierProductMetricsProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supplier products metrics!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_PRODUCTS_METRICS.module],
    queryFn: () => getSupplierMetrics(),
  })
}

export const useForecastsAndInventoriesList = ({
  params,
  isEnable = true,
}: {
  params?: Record<string, string | number>
  isEnable?: boolean
}) => {
  const getForecastsAndInventoriesList = async (): Promise<{
    success: boolean
    message: string
    data?: ForecastsAndInventoriesWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (
          copyParams?.[key] &&
          ['catalogProductUuid', 'isRegulated'].includes(key)
        ) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }
        return acc
      }, {})

      const { data } = await api.get(BFF_FORECASTS_AND_INVENTORIES_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Forecasts and inventories fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as ForecastsAndInventoriesWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get forecasts and inventories!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_FORECASTS_AND_INVENTORIES.module, params],
    enabled: isEnable,
    queryFn: () => getForecastsAndInventoriesList(),
  })
}

export const useForecastList = ({
  params,
  isEnable = true,
}: {
  params?: Record<string, string | number>
  isEnable?: boolean
}) => {
  const getForecastList = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierForecastListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && forecastPermittedParams.includes(key)) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }
        return acc
      }, {})

      const { data } = await api.get(BFF_FORECASTS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Forecasts fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as SupplierForecastListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Forecasts!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_FORECAST.module, params],
    enabled: isEnable,
    queryFn: () => getForecastList(),
  })
}

export const useForecastDetail = (uuid?: string) => {
  const getForecastDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierForecastProps
  }> => {
    try {
      const { data } = await api.get(BFF_FORECAST_URL(uuid || ''))

      return {
        success: true,
        message: 'forecast fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplierForecastProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get forecast!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_FORECAST.module, uuid],
    queryFn: () => getForecastDetail(),
    enabled: !!uuid,
  })
}

export const useUpdateForecast = () => {
  const updateForecast = async (
    payload: Partial<SupplierForecastProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierForecastProps
  }> => {
    try {
      const { data } = await api.patch(`${BFF_FORECASTS_URL}${payload.uuid}/`, {
        ...payload,
        uuid: undefined,
      })

      return {
        success: true,
        message: 'Supplier products updated successfully!',
        data: convertObjectToCamelCase(data) as SupplierForecastProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update supplier products!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_FORECAST.module],
    mutationFn: (payload: Partial<SupplierForecastProps>) =>
      updateForecast(payload),
  })
}

export const useDeleteForecast = () => {
  const queryClient = useQueryClient()

  const deleteForecast = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierForecastProps
  }> => {
    try {
      const { data } = await api.delete(`${BFF_FORECASTS_URL}${uuid}/`)

      return {
        success: true,
        message: 'Supplier forecast deleted successfully!',
        data: convertObjectToCamelCase(data) as SupplierForecastProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to deleted supplier forecast!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_FORECAST.module],
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERIES_ADMIN.BFF_FORECAST.module,
      ] as InvalidateQueryFilters)
    },
    mutationFn: (uuid: string) => deleteForecast(uuid),
  })
}
